"use client";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Button, Select} from "@mantine/core";
import ProductGrid from "@/components/product/ProductGrid";

export default function ProductFilters({initialProducts,initialHasMore,brands,types,q = '',labelMap}){
    const initialized = useRef(false)
    const [products, setProducts] = useState(initialProducts);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(initialHasMore);
    const [loading, setLoading] = useState(false);

    const brandOptions = useMemo(() => {
        return brands?.map(brand => ({
            value: brand.brand_id.toString(),
            label: brand.brand_name,
        }))
    }, [brands])

    const typeOptions = useMemo(() => {
        return types?.map(ptype => ({
            value: ptype.product_type_id.toString(),
            label: ptype.product_type_name,
        }))

    }, [types])


    const [selectedBrand, setSelectedBrand] = useState(null);
    const [selectedProductType, setSelectedProductType] = useState(null);
    const [selectedSortBy, setSelectedSortBy] = useState('created-descending');
    const [queryString, setQueryString] = useState(q);

    const sortOptions = [
        { value: 'title-ascending', label: 'Alphabetically, A-Z' },
        { value: 'title-descending', label: 'Alphabetically, Z-A' },
        { value: 'price-ascending', label: 'Price, low to high' },
        { value: 'price-descending', label: 'Price, high to low' },
        { value: 'created-ascending', label: 'Date, old to new' },
        { value: 'created-descending', label: 'Date, new to old' },
    ];

    const fetchProducts = useCallback(async ({nextPage = 1,append = false} = {}) => {
        try {
            setLoading(true);
            const query =
                new URLSearchParams({
                    page: nextPage,
                    ...(selectedBrand && {brand: selectedBrand}),
                    ...(selectedProductType && {type: selectedProductType}),
                    ...(selectedSortBy && {sort: selectedSortBy}),
                    ...(queryString && {q: queryString}),
                });

            const res = await fetch(`/api/products?${query}`);
            const data = await res.json();
            const newProducts = data?.products?.data || [];
            if (append) {
                setProducts(prev => [...prev, ...newProducts]);
            } else {
                setProducts(newProducts);
            }
            setHasMore(!!data?.products?.next_page_url);
            setPage(nextPage);
        } catch(error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    },[selectedBrand,selectedProductType,selectedSortBy,queryString]);

    useEffect(()=>{
        if (!initialized.current) {
            initialized.current = true
            return
        }
        (async () => {
            await fetchProducts({
                nextPage: 1,
                append: false,
            })
        })()
    },[fetchProducts])


    async function loadMore() {
        if (loading) return
        await fetchProducts({
            nextPage: page + 1,
            append: true
        });
    }

    return (
        <div>
            <div className={'grid grid-cols-24 gap-1'}>
                <div className={'col-span-12 lg:col-span-5'}>
                    <Select label={'Brand'} data={brandOptions} value={selectedBrand} withCheckIcon checkIconPosition="right"
                            onChange={setSelectedBrand} placeholder={'Select Brand'} clearable></Select>
                </div>
                <div className={'col-span-12 lg:col-span-5'}>
                    <Select label={'Product Type'} data={typeOptions} value={selectedProductType} withCheckIcon checkIconPosition="right"
                            onChange={setSelectedProductType} placeholder={'Select Type'} clearable></Select>
                </div>
                <div className={'lg:col-span-9'}></div>
                <div className={'col-span-24 lg:col-span-5'}>
                    <Select label={'Sort By'} data={sortOptions} value={selectedSortBy} onChange={setSelectedSortBy}></Select>
                </div>
            </div>
            <ProductGrid products={products} labelMap={labelMap} />
            {hasMore && (
                <div className={'text-center mt-6'}>
                    <Button onClick={loadMore} size={'sm'} disabled={loading} className={'rounded'}>
                        {loading
                            ? 'Loading...'
                            : 'Load More'}
                    </Button>
                </div>
            )}
        </div>
    )

}